import { ProjectCaseStudy } from '../types/portfolio';

export const projectCaseStudies: ProjectCaseStudy[] = [
  {
    id: 'doctor-terra-wellness-app',
    slug: 'doctor-terra-wellness-app',
    title: 'Doctor Terra All-in-One Wellness & Membership Platform',
    category: 'Development Services',
    clientIndustry: 'Digital Health & Member Marketplace',
    summary: 'An integrated mobile platform empowering members to manage personal health records, access digital shopping, and track referral earnings in one seamless application.',
    challenge: 'Doctor Terra members needed a centralized digital hub to monitor personal health programs, browse curated wellness products, and manage membership commission payouts without friction.',
    solution: 'Technook engineered a high-performance cross-platform mobile application featuring secure health profile vaults, an integrated digital marketplace, and automated referral earnings tracking.',
    results: [
      'Over 50,000 active membership profiles onboarded',
      'Seamless multi-tier commission & earnings tracking engine',
      '99.9% uptime SLA across peak promotional health drives',
    ],
    metrics: [
      { label: 'Active Members', value: '50,000+' },
      { label: 'Platform SLA', value: '99.9%' },
      { label: 'App Store Rating', value: '4.9/5' },
    ],
    techStack: [],
    imageSrc: '/assets/screens/Doctor-Terra-App.png',
    isFeatured: true,
  },
  {
    id: 'web3-blockchain-asset-registry',
    slug: 'web3-blockchain-asset-registry',
    title: 'Decentralized Asset & Smart Contract Audit Registry',
    category: 'Web3 & Blockchain',
    clientIndustry: 'FinTech & Decentralized Ecosystems',
    summary: 'An enterprise Web3 ledger platform featuring automated smart contract security auditing, decentralized tokenized asset vaults, tamper-proof audit trails, and multi-signature crypto treasury management.',
    challenge: 'FinTech platforms and decentralized protocols required zero-trust security auditing, automated smart contract execution, and transparent multi-sig treasury monitoring.',
    solution: 'Engineered a secure Web3 decentralized portal integrating automated smart contract verification, real-time transaction telemetry, and multi-signature treasury wallet controls.',
    results: [
      'Over $12.5M+ in decentralized digital assets secured and audited',
      'Zero smart contract security exploits recorded across all deployed protocols',
      'High-speed ledger processing handling over 50,000 verified daily transactions',
    ],
    metrics: [
      { label: 'Assets Secured', value: '$12.5M+ TVL' },
      { label: 'Smart Contract Audit', value: '100% Secure' },
      { label: 'Ledger Throughput', value: '50,000+/Day' },
    ],
    techStack: [],
    imageSrc: '/assets/screens/Doctor-Terra-App.png',
    isFeatured: true,
  },
  {
    id: 'enterprise-emr-health-system',
    slug: 'enterprise-emr-health-system',
    title: 'Enterprise EMR & Digital Hospital Information Management System (HIMS)',
    category: 'Healthcare & EMR',
    clientIndustry: 'Healthcare & Clinical Systems',
    summary: 'A DOH-compliant Electronic Medical Records (EMR) and hospital management platform featuring digital patient health vaults, e-prescriptions, ICD-10 medical billing, PhilHealth claim automation, and doctor consultation scheduling.',
    challenge: 'Hospitals and medical clinics faced fragmented patient records, slow paper-based chart retrieval, manual PhilHealth claim filing delays, and consultation scheduling conflicts.',
    solution: 'Engineered an integrated EMR & HIMS cloud suite providing secure role-based access for doctors, triage nurses, diagnostic labs, pharmacies, and billing administrators with automated ICD-10 coding and electronic prescription generation.',
    results: [
      'Over 250,000 patient electronic health records secured and indexed',
      'PhilHealth insurance claim filing turnaround reduced to under 24 hours',
      '55% reduction in patient consultation waiting time and clinical administrative SLA',
    ],
    metrics: [
      { label: 'Patient EMR Records', value: '250,000+' },
      { label: 'PhilHealth SLA', value: '< 24 Hours' },
      { label: 'Clinical Workflow', value: '+55%' },
    ],
    techStack: [],
    imageSrc: '/assets/screens/Doctor-Terra-App.png',
    isFeatured: true,
  },
  {
    id: 'terra-express-delivery-logistics-platform',
    slug: 'terra-express-delivery-logistics-platform',
    title: 'Terra Express Delivery & Multi-Vehicle Fleet Logistics Platform',
    category: 'Delivery & Logistics',
    clientIndustry: 'Logistics & Supply Chain Mobility',
    summary: 'An end-to-end multi-vehicle delivery and dispatch platform featuring real-time GPS driver tracking, automated order dispatching, proof-of-delivery (POD) digital signature capture, and merchant COD payout reconciliation.',
    challenge: 'Logistics operators and local merchants needed an integrated dispatch engine to handle on-demand parcel deliveries, food orders, and freight shipping with transparent driver tracking.',
    solution: 'Technook built a complete logistics ecosystem comprising a Merchant Web Portal, Customer Tracking Web App, Rider Mobile App (Android/iOS), and an automated Dispatch Command Console.',
    results: [
      'Over 1,000,000 successful parcel and food delivery orders fulfilled',
      'Automated order dispatching matching riders to nearest merchants in under 90 seconds',
      'Integrated Cash-on-Delivery (COD) reconciliation engine supporting 5,000+ active fleet drivers',
    ],
    metrics: [
      { label: 'Orders Fulfilled', value: '1,000,000+' },
      { label: 'Dispatch Speed', value: '< 90 Secs' },
      { label: 'Active Fleet', value: '5,000+ Drivers' },
    ],
    techStack: [],
    imageSrc: '/assets/screens/Doctor-Terra-App.png',
    isFeatured: true,
  },
  {
    id: 'lgu-real-property-tax-system',
    slug: 'lgu-real-property-tax-system',
    title: 'LGU Automated Real Property Tax & Assessment Registry System',
    category: 'Public Sector & LGU',
    clientIndustry: 'Public Governance & Municipal Finance',
    summary: 'An enterprise municipal tax administration platform featuring automated property assessment, tax declaration tracking, online clearance issuance, and transparent revenue auditing for local government units.',
    challenge: 'Municipal assessors and treasurers faced manual paper backlogs, slow tax clearance verification, and delayed property tax assessment updates across provincial offices.',
    solution: 'Technook engineered a secure, cloud-enabled Real Property Tax (RPT) Registry featuring automated tax declaration indexing, electronic receipt generation, audit trail logging, and public online tax payment portals.',
    results: [
      '68% increase in municipal RPT collection efficiency in the first fiscal year',
      'Tax clearance verification time reduced from 5 business days to under 2 minutes',
      '100% compliance with COA audit standards and local government tax codes',
    ],
    metrics: [
      { label: 'Collection Efficiency', value: '+68%' },
      { label: 'Tax Clearance SLA', value: '< 2 Mins' },
      { label: 'COA Compliance', value: '100%' },
    ],
    techStack: [],
    imageSrc: '/assets/screens/Doctor-Terra-App.png',
    isFeatured: true,
  },
  {
    id: 'lgu-business-permit-bpls',
    slug: 'lgu-business-permit-bpls',
    title: 'Smart LGU Business Permit & Automated Licensing Portal (BPLS)',
    category: 'Public Sector & LGU',
    clientIndustry: 'Municipal Administration & E-Governance',
    summary: 'End-to-end digital portal streamlining new business permit applications, unified clearance approvals, automated fee computations, and QR-verifiable digital Mayor’s Permit generation.',
    challenge: 'Local business owners experienced long queuing lines during annual permit renewal season, manual inter-departmental approval bottlenecks, and lack of real-time application tracking.',
    solution: 'Engineered a unified E-BPLS portal enabling business applicants to upload documentary requirements online, receive automated fee assessments, pay via integrated payment gateways, and download QR-validated permits.',
    results: [
      'Over 12,000 local business permits processed digitally during peak renewal month',
      'Average permit processing turnaround reduced from 7 days to 24 hours',
      '42% increase in timely annual business tax compliance',
    ],
    metrics: [
      { label: 'Permits Processed', value: '12,000+' },
      { label: 'Processing SLA', value: '24 Hours' },
      { label: 'Renewal Compliance', value: '+42%' },
    ],
    techStack: [],
    imageSrc: '/assets/screens/Doctor-Terra-App.png',
    isFeatured: true,
  },
  {
    id: 'lgu-citizen-disaster-command',
    slug: 'lgu-citizen-disaster-command',
    title: 'Municipal Citizen Care & Emergency Command Portal',
    category: 'Public Sector & LGU',
    clientIndustry: 'Smart Cities & Citizen Governance',
    summary: 'A centralized citizen engagement and emergency response portal enabling real-time incident reporting, barangay certificate requests, flood monitoring alerts, and municipal announcement broadcasting.',
    challenge: 'Citizens lacked a direct mobile channel to submit barangay inquiries, request official certificates, or alert municipal responders during typhoons and emergency disasters.',
    solution: 'Technook built a multi-channel LGU Citizen App connected to a centralized Municipal Command & Control Center dashboard with real-time GPS incident mapping and SMS dispatch routing.',
    results: [
      'Over 100,000 registered municipal citizens actively using the portal',
      'Emergency response dispatch time cut down to under 5 minutes',
      'Automated barangay clearance processing for over 50 local barangays',
    ],
    metrics: [
      { label: 'Active Citizens', value: '100,000+' },
      { label: 'Dispatch SLA', value: '< 5 Mins' },
      { label: 'Barangays Linked', value: '50+' },
    ],
    techStack: [],
    imageSrc: '/assets/screens/Doctor-Terra-App.png',
    isFeatured: true,
  },
  {
    id: 'technook-b2b-lead-engine',
    slug: 'technook-b2b-lead-engine',
    title: 'Technook Enterprise Outbound Lead Pipeline Engine',
    category: 'Lead Generation',
    clientIndustry: 'B2B Software & Enterprise Tech',
    summary: 'Targeted outbound prospecting, cold calling, and executive appointment setting campaign for high-value tech decision makers.',
    challenge: 'Enterprise technology buyers require multi-channel engagement and highly personalized technical consultation offers before booking strategy meetings.',
    solution: 'Deployed Technook’s dedicated sales support team to curate B2B decision-maker lists, run multi-touch campaigns, and manage executive calendars.',
    results: [
      '140+ C-level qualified appointments set in 90 days',
      '$1.4M+ in validated enterprise sales pipeline',
    ],
    metrics: [
      { label: 'Meetings Booked', value: '140+' },
      { label: 'Pipeline Generated', value: '$1.4M+' },
    ],
    techStack: [],
    imageSrc: '/assets/screens/Doctor-Terra-App.png',
    isFeatured: true,
  },
];
